/**
 * Entity Activity Report PDF
 * Generates a professional PDF report for a single entity's activity
 */

import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { baseStyles, colors, getConnectionColor } from './styles';
import {
  ClassificationBanner,
  PageHeader,
  Section,
  StatsRow,
  Badge,
  PageFooter,
  EmptyState,
} from './PDFComponents';

// Additional styles specific to entity report
const styles = StyleSheet.create({
  connectionTypeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    marginTop: 10,
  },
  connectionTypeLabel: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  connectionCount: {
    fontSize: 9,
    color: colors.gray500,
    marginLeft: 8,
  },
  connectionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
    borderLeftWidth: 2,
    marginBottom: 0,
    backgroundColor: colors.gray100,
  },
  connectionLabel: {
    fontSize: 9,
    color: colors.gray700,
    flexShrink: 1,
    flexGrow: 1,
    marginRight: 10,
    maxWidth: '85%',
  },
  connectionValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.gray600,
    width: 40,
    textAlign: 'right',
    flexShrink: 0,
  },
  noteCard: {
    backgroundColor: colors.gray100,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
    borderLeftWidth: 3,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  noteTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.gray800,
    flex: 1,
  },
  noteContent: {
    fontSize: 9,
    color: colors.gray600,
    lineHeight: 1.4,
  },
  noteMeta: {
    fontSize: 8,
    color: colors.gray400,
    marginTop: 4,
  },
  imageContainer: {
    marginBottom: 10,
    alignItems: 'center',
  },
  imageCaption: {
    fontSize: 8,
    color: colors.gray400,
    marginTop: 2,
    textAlign: 'center',
  },
  twoImageRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
    justifyContent: 'center',
  },
  singleImage: {
    flex: 1,
    alignItems: 'center',
    maxWidth: '48%',
  },
  connectionGroup: {
    marginBottom: 10,
  },
  connectionGroupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    marginTop: 8,
    backgroundColor: '#f9fafb',
    padding: 4,
    borderRadius: 2,
  },
  // SMS Message styles
  messageCard: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#22c55e',
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  messageContact: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.primaryDark,
  },
  messageTimestamp: {
    fontSize: 8,
    color: colors.gray400,
  },
  messageContent: {
    fontSize: 9,
    color: colors.gray700,
    lineHeight: 1.4,
    paddingVertical: 4,
  },
  messageDirection: {
    fontSize: 7,
    color: '#ffffff',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 2,
  },
  outgoingBadge: {
    backgroundColor: '#3b82f6',
  },
  incomingBadge: {
    backgroundColor: '#22c55e',
  },
  // Call record styles
  callCard: {
    marginBottom: 6,
    padding: 6,
    backgroundColor: '#fefce8',
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#eab308',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  callInfo: {
    flex: 1,
  },
  callDuration: {
    fontSize: 8,
    color: colors.gray500,
    marginLeft: 8,
  },
});

// ============ TYPES ============

export interface EntityConnection {
  type: string;
  targetLabel: string;
  targetId: string;
  count: number;
}

export interface SmsMessage {
  id?: string;
  targetLabel: string;
  targetId: string;
  message: string;
  timestamp?: string;
  direction?: 'incoming' | 'outgoing';
}

export interface CallRecord {
  id?: string;
  targetLabel: string;
  targetId: string;
  duration?: number;
  timestamp?: string;
  direction?: 'incoming' | 'outgoing';
}

export interface EntityNote {
  id: string;
  title: string;
  content: string;
  priority?: 'critical' | 'high' | 'normal' | 'low';
  created_at?: string;
  updated_at?: string;
}

export interface EntityReportData {
  entityId: string;
  entityLabel: string;
  entityType?: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  connections: EntityConnection[];
  notes: EntityNote[];
  smsMessages?: SmsMessage[];
  callRecords?: CallRecord[];
  stats?: {
    totalConnections: number;
    uniqueContacts: number;
    uniqueLocations: number;
    uniqueWebsites: number;
  };
  images?: {
    dashboard?: string;
    graph?: string;
    map?: string;
    charts?: string;
    rightPanel?: string;
  };
  classification?: string;
}

// ============ HELPER FUNCTIONS ============

const formatDateRange = (start: Date, end: Date): string => {
  const formatDate = (d: Date) => {
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  return `${formatDate(start)} - ${formatDate(end)}`;
};

const getConnectionTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    call: 'Phone Calls',
    sms: 'Text Messages (SMS)',
    browsing: 'Websites Visited',
    app: 'Applications Used',
    dwell: 'Locations (Dwells)',
  };
  return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

const getPriorityColor = (priority?: string): string => {
  const colorMap: Record<string, string> = {
    critical: colors.critical,
    high: colors.high,
    normal: colors.normal,
    low: colors.low,
  };
  return colorMap[priority || 'normal'] || colors.normal;
};

const groupConnectionsByType = (connections: EntityConnection[]): Record<string, EntityConnection[]> => {
  return connections.reduce((acc, conn) => {
    if (!acc[conn.type]) acc[conn.type] = [];
    acc[conn.type].push(conn);
    return acc;
  }, {} as Record<string, EntityConnection[]>);
};

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) return 'Unknown time';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return timestamp;
  }
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return 'Unknown duration';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// ============ MAIN COMPONENT ============

export const EntityReportPDF: React.FC<{ data: EntityReportData }> = ({ data }) => {
  const {
    entityLabel,
    dateRange,
    connections,
    notes,
    smsMessages,
    callRecords,
    stats,
    images,
    classification,
  } = data;

  const connectionsByType = groupConnectionsByType(connections);
  const connectionTypes = Object.keys(connectionsByType);
  
  // Calculate stats if not provided
  const calculatedStats = stats || {
    totalConnections: connections.reduce((sum, c) => sum + c.count, 0),
    uniqueContacts: new Set(connections.filter(c => c.type === 'call' || c.type === 'sms').map(c => c.targetId)).size,
    uniqueLocations: connectionsByType['dwell']?.length || 0,
    uniqueWebsites: connectionsByType['browsing']?.length || 0,
  };

  return (
    <Document>
      {/* PAGE 1: Overview and Stats */}
      <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page}>
        {classification && <ClassificationBanner classification={classification} position="top" />}
        
        <PageHeader 
          title={`Entity Activity Report`}
          subtitle={entityLabel}
          meta={`Report Period: ${formatDateRange(dateRange.start, dateRange.end)} | Generated: ${new Date().toLocaleString()}`}
        />

        {/* Quick Stats */}
        <Section title="Activity Overview">
          <StatsRow stats={[
            { value: calculatedStats.totalConnections.toLocaleString(), label: 'Total Interactions', color: colors.primary },
            { value: calculatedStats.uniqueContacts, label: 'Unique Contacts', color: colors.call },
            { value: calculatedStats.uniqueLocations, label: 'Locations', color: colors.dwell },
            { value: calculatedStats.uniqueWebsites, label: 'Websites', color: colors.browsing },
          ]} />
          
          {/* Connection type breakdown */}
          <View style={{ marginTop: 10 }}>
            <Text style={baseStyles.sectionSubtitle}>Connection Breakdown</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 5 }}>
              {connectionTypes.map(type => (
                <View key={type} style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: 4, 
                    backgroundColor: getConnectionColor(type),
                    marginRight: 4,
                  }} />
                  <Text style={baseStyles.textSmall}>
                    {getConnectionTypeLabel(type)}: {connectionsByType[type].length}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Section>

        {/* Visual Overview */}
        {(images?.dashboard || images?.graph || images?.map || images?.charts || images?.rightPanel) && (
          <Section title="Visual Overview">
            {/* Full Dashboard Screenshot (preferred) */}
            {images?.dashboard && (
              <View style={styles.imageContainer}>
                <Image src={images.dashboard} style={{ width: 535, height: 320, objectFit: 'contain' }} />
                <Text style={styles.imageCaption}>Dashboard Overview</Text>
              </View>
            )}
            
            {/* Individual images only if no dashboard capture */}
            {!images?.dashboard && (
              <>
                {/* Network Graph */}
                {images?.graph && (
                  <View style={styles.imageContainer}>
                    <Image src={images.graph} style={{ width: 535, height: 280, objectFit: 'contain' }} />
                    <Text style={styles.imageCaption}>Network Graph</Text>
                  </View>
                )}
                
                {/* Location Map */}
                {(images?.map || images?.rightPanel) && (
                  <View style={styles.imageContainer}>
                    <Image 
                      src={images.map || images.rightPanel} 
                      style={{ width: 535, height: 280, objectFit: 'contain' }} 
                    />
                    <Text style={styles.imageCaption}>{images.map ? 'Location Map' : 'Activity Data'}</Text>
                  </View>
                )}

                {/* Charts if available */}
                {images?.charts && (
                  <View style={styles.imageContainer}>
                    <Image src={images.charts} style={{ width: 535, height: 200, objectFit: 'contain' }} />
                    <Text style={styles.imageCaption}>Activity Charts</Text>
                  </View>
                )}
              </>
            )}
          </Section>
        )}

        <PageFooter />
        {classification && <ClassificationBanner classification={classification} position="bottom" />}
      </Page>

      {/* PAGE 2+: Connections Detail */}
      <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page} wrap>
        {classification && <ClassificationBanner classification={classification} position="top" />}
        
        <Section title={`All Connections (${connections.length} unique)`}>
          {connectionTypes.length === 0 ? (
            <EmptyState message="No connections found for this entity in the selected time range." />
          ) : (
            connectionTypes.map(type => {
              const typeConnections = connectionsByType[type].sort((a, b) => b.count - a.count);
              const color = getConnectionColor(type);
              const MAX_VISIBLE = 50; // Limit items per type to prevent overly long pages
              const hasMore = typeConnections.length > MAX_VISIBLE;
              const visibleConnections = hasMore ? typeConnections.slice(0, MAX_VISIBLE) : typeConnections;
              
              return (
                <View key={type} style={styles.connectionGroup}>
                  <View style={styles.connectionGroupHeader} wrap={false}>
                    <View style={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: 5, 
                      backgroundColor: color,
                      marginRight: 6,
                    }} />
                    <Text style={[styles.connectionTypeLabel, { color }]}>
                      {getConnectionTypeLabel(type)}
                    </Text>
                    <Text style={styles.connectionCount}>
                      ({typeConnections.length} unique, {typeConnections.reduce((s, c) => s + c.count, 0)} total)
                    </Text>
                  </View>
                  
                  {visibleConnections.map((conn, idx) => (
                    <View key={idx} style={[styles.connectionItem, { borderLeftColor: color }]} wrap={false}>
                      <Text style={styles.connectionLabel}>
                        {conn.targetLabel || conn.targetId}
                      </Text>
                      <Text style={styles.connectionValue}>{conn.count}x</Text>
                    </View>
                  ))}
                  
                  {hasMore && (
                    <Text style={{ fontSize: 8, color: colors.gray400, marginTop: 4, fontStyle: 'italic' }}>
                      ... and {typeConnections.length - MAX_VISIBLE} more {getConnectionTypeLabel(type).toLowerCase()}
                    </Text>
                  )}
                </View>
              );
            })
          )}
        </Section>

        <PageFooter />
        {classification && <ClassificationBanner classification={classification} position="bottom" />}
      </Page>

      {/* PAGE 3+: Notes (if any) */}
      {notes.length > 0 && (
        <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page}>
          {classification && <ClassificationBanner classification={classification} position="top" />}
          
          <Section title={`Analyst Notes (${notes.length})`}>
            {notes.map((note, index) => (
              <View 
                key={note.id || index} 
                style={[styles.noteCard, { borderLeftColor: getPriorityColor(note.priority) }]}
                wrap={false}
              >
                <View style={styles.noteHeader}>
                  {note.priority && note.priority !== 'normal' && (
                    <Badge 
                      text={note.priority.toUpperCase()} 
                      backgroundColor={getPriorityColor(note.priority)}
                    />
                  )}
                  <Text style={styles.noteTitle}>{note.title}</Text>
                </View>
                <Text style={styles.noteContent}>{note.content}</Text>
                {note.created_at && (
                  <Text style={styles.noteMeta}>
                    Created: {new Date(note.created_at).toLocaleDateString()} 
                    {note.updated_at && note.updated_at !== note.created_at && 
                      ` | Updated: ${new Date(note.updated_at).toLocaleDateString()}`
                    }
                  </Text>
                )}
              </View>
            ))}
          </Section>

          <PageFooter />
          {classification && <ClassificationBanner classification={classification} position="bottom" />}
        </Page>
      )}

      {/* PAGE: Phone Calls - Summary and Details */}
      {callRecords && callRecords.length > 0 && (
        <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page} wrap>
          {classification && <ClassificationBanner classification={classification} position="top" />}
          
          <Section title="Phone Calls">
            {/* Summary */}
            <View style={{ backgroundColor: colors.gray100, padding: 8, borderRadius: 4, marginBottom: 12 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', color: colors.gray800, marginBottom: 4 }}>
                Summary
              </Text>
              <View style={{ flexDirection: 'row', gap: 20 }}>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Total Calls</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.call }}>{callRecords.length}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Outgoing</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#3b82f6' }}>
                    {callRecords.filter(c => c.direction === 'outgoing').length}
                  </Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Incoming</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#22c55e' }}>
                    {callRecords.filter(c => c.direction === 'incoming').length}
                  </Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Unique Contacts</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.gray700 }}>
                    {new Set(callRecords.map(c => c.targetId)).size}
                  </Text>
                </View>
              </View>
            </View>

            {/* Details */}
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.gray700, marginBottom: 6 }}>
              Call Details (sorted by most recent)
            </Text>
            
            {callRecords.slice(0, 50).map((call, index) => (
              <View key={call.id || index} style={styles.callCard} wrap={false}>
                <View style={styles.callInfo}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text 
                      style={[
                        styles.messageDirection, 
                        call.direction === 'outgoing' ? styles.outgoingBadge : styles.incomingBadge
                      ]}
                    >
                      {call.direction === 'outgoing' ? '→ OUT' : '← IN'}
                    </Text>
                    <Text style={styles.messageContact}>{call.targetLabel || call.targetId}</Text>
                  </View>
                  <Text style={styles.messageTimestamp}>{formatTimestamp(call.timestamp)}</Text>
                </View>
                {call.duration !== undefined && (
                  <Text style={styles.callDuration}>{formatDuration(call.duration)}</Text>
                )}
              </View>
            ))}
            
            {callRecords.length > 50 && (
              <Text style={{ fontSize: 8, color: colors.gray400, marginTop: 4, fontStyle: 'italic' }}>
                ... and {callRecords.length - 50} more calls not shown
              </Text>
            )}
          </Section>

          <PageFooter />
          {classification && <ClassificationBanner classification={classification} position="bottom" />}
        </Page>
      )}

      {/* PAGE: Text Messages - Summary and Details */}
      {smsMessages && smsMessages.length > 0 && (
        <Page size="A4" style={classification ? baseStyles.pageWithBanner : baseStyles.page} wrap>
          {classification && <ClassificationBanner classification={classification} position="top" />}
          
          <Section title="Text Messages (SMS)">
            {/* Summary */}
            <View style={{ backgroundColor: colors.gray100, padding: 8, borderRadius: 4, marginBottom: 12 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', color: colors.gray800, marginBottom: 4 }}>
                Summary
              </Text>
              <View style={{ flexDirection: 'row', gap: 20 }}>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Total Messages</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.sms }}>{smsMessages.length}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Sent</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#3b82f6' }}>
                    {smsMessages.filter(m => m.direction === 'outgoing').length}
                  </Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Received</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#22c55e' }}>
                    {smsMessages.filter(m => m.direction === 'incoming').length}
                  </Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: colors.gray500 }}>Unique Contacts</Text>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.gray700 }}>
                    {new Set(smsMessages.map(m => m.targetId)).size}
                  </Text>
                </View>
              </View>
            </View>

            {/* Details */}
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.gray700, marginBottom: 6 }}>
              Message Details (sorted by most recent)
            </Text>
            
            {smsMessages.slice(0, 50).map((msg, index) => (
              <View key={msg.id || index} style={styles.messageCard} wrap={false}>
                <View style={styles.messageHeader}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text 
                      style={[
                        styles.messageDirection, 
                        msg.direction === 'outgoing' ? styles.outgoingBadge : styles.incomingBadge
                      ]}
                    >
                      {msg.direction === 'outgoing' ? '→ SENT' : '← RECV'}
                    </Text>
                    <Text style={styles.messageContact}>{msg.targetLabel || msg.targetId}</Text>
                  </View>
                  <Text style={styles.messageTimestamp}>{formatTimestamp(msg.timestamp)}</Text>
                </View>
                <Text style={styles.messageContent}>{msg.message}</Text>
              </View>
            ))}
            
            {smsMessages.length > 50 && (
              <Text style={{ fontSize: 8, color: colors.gray400, marginTop: 4, fontStyle: 'italic' }}>
                ... and {smsMessages.length - 50} more messages not shown
              </Text>
            )}
          </Section>

          <PageFooter />
          {classification && <ClassificationBanner classification={classification} position="bottom" />}
        </Page>
      )}
    </Document>
  );
};

export default EntityReportPDF;

